import { CustomerService, resolveCustomerAvatarImageUrl } from "../../services";
import {
  getCachedCustomerAvatarUrl,
  setCachedCustomerAvatarUrl,
  clearCustomerAvatarCacheEntry,
  bumpCustomerAvatarCacheRevision,
} from "./customerAvatarCache";

function withCacheBuster(url) {
  if (!url) return null;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${Date.now()}`;
}

/**
 * Resolve a customer avatar image URL, using session/memory cache unless bypassed.
 */
export async function fetchCustomerAvatarImageUrl(customerId, { bypassCache = false } = {}) {
  if (!customerId) return null;

  if (!bypassCache) {
    const cached = getCachedCustomerAvatarUrl(customerId);
    if (cached !== undefined) return cached;
  }

  try {
    const response = await CustomerService.getCustomerAvatarUrl(customerId);
    const rawUrl = response?.data?.url || response?.data?.absoluteUrl;
    const url = rawUrl ? resolveCustomerAvatarImageUrl(rawUrl) : null;
    setCachedCustomerAvatarUrl(customerId, url);
    return url;
  } catch {
    setCachedCustomerAvatarUrl(customerId, null);
    return null;
  }
}

/**
 * After avatar upload/delete: clear caches, fetch the latest URL, store it, and notify listeners.
 * Appends a cache-buster query param so browsers reload legacy stable filenames.
 */
export async function refreshCustomerAvatarCache(customerId) {
  if (!customerId) return null;

  clearCustomerAvatarCacheEntry(customerId);

  try {
    const response = await CustomerService.getCustomerAvatarUrl(customerId, { bypassCache: true });
    const rawUrl = response?.data?.url || response?.data?.absoluteUrl;
    const resolved = rawUrl ? resolveCustomerAvatarImageUrl(rawUrl) : null;
    const url = withCacheBuster(resolved);
    setCachedCustomerAvatarUrl(customerId, url);
    bumpCustomerAvatarCacheRevision(customerId);
    return url;
  } catch {
    setCachedCustomerAvatarUrl(customerId, null);
    bumpCustomerAvatarCacheRevision(customerId);
    return null;
  }
}
